import {
  createTrackingConsentManager,
  type EffectiveTrackingConsentPolicy,
  type ResourceTrackingConsentScope,
  type TrackingConsentManager
} from '@goldcast/tracking-consent';

import '@goldcast/tracking-consent/styles.css';

let manager: TrackingConsentManager | null = null;

/**
 * Builds the shared consent manager from the effective event policy.
 * Returns null when the flag is off, the org has not enabled consent, or the
 * scope ID is missing — in those cases tracking runs without a gate, matching
 * pre-GDPR-feature behaviour.
 */
export function initializeTrackingConsent(
  policy: EffectiveTrackingConsentPolicy | null | undefined,
  scope: ResourceTrackingConsentScope,
  language?: string | null
): TrackingConsentManager | null {
  if (new URLSearchParams(window.location.search).get('record_mode') === 'true') {
    manager = null;
    return null;
  }
  if (!policy || policy.enabled !== true || !scope?.id) {
    manager = null;
    return null;
  }
  // No floating settings button here: it is fixed to a screen corner, and every
  // corner of the event UI is occupied by the stage controls, the sidebar or the
  // navbar. The profile dropdown carries the entry point instead, so consent
  // stays withdrawable on every page without covering anything.
  manager = createTrackingConsentManager({
    policy,
    scope,
    document,
    location,
    language
  });
  manager.mountBanner();
  return manager;
}

/** Whether tracking for the given category may run now. Defaults to true when no consent manager is active (flag off or org not configured). */
export function canTrack(category: string): boolean {
  return manager?.canTrack(category) ?? true;
}

export function getTrackingConsentManager(): TrackingConsentManager | null {
  return manager;
}
